import type { AuctionSlot } from "@/lib/auction-types"

/**
 * Discriminated union describing what the current viewer should see for an
 * auction. Centralizes the "can this user pay?" / "is this user the winner?"
 * predicate that previously drifted across desktop panel, mobile bid bar,
 * and the winner checkout sheet (see issue #156, drift bug #29).
 *
 * Server-side `performSettle` and `notifyAuctionEnded` remain authoritative
 * for enforcement; this is the read-side projection only.
 */
export type ViewerAuctionState =
  | { kind: "scheduled" }
  | { kind: "live"; canBid: boolean; reserveMet: boolean | null }
  | { kind: "ended_no_bids" }
  | { kind: "ended_reserve_not_met"; wasHighestBidder: boolean }
  | {
      kind: "ended_won_pending_payment"
      deadline: string | null
      canPay: boolean
      existingOrderId: string | null
    }
  | { kind: "ended_won_settled"; orderId: string | null }
  | { kind: "ended_payment_expired" }
  | { kind: "ended_lost"; winnerKnown: boolean }
  | {
      kind: "ended_seller"
      subKind: "sold" | "no_bids" | "reserve_not_met" | "settled" | "payment_expired"
    }

/**
 * SSR baseline for the auction (the props passed to AuctionPanel /
 * MobileProductPage). Mirrors the fields of `AuctionData` that the FSM cares
 * about — kept structurally compatible so callers can pass `auction` directly.
 */
export interface ViewerAuctionBaseline {
  startPrice: number
  reservePrice: number | null
  endAt: string | null
  status: string
  highestBid: number | null
  highestBidderId: string | null
  bidCount: number
  paymentDeadline: string | null
}

export interface DeriveViewerAuctionStateInputs {
  /** Realtime slot from `useAuction(listingId)`, or null before subscription. */
  slot: AuctionSlot | null
  /** SSR baseline auction data (used as fallback when realtime slot is empty). */
  auction: ViewerAuctionBaseline
  /** Auth'd viewer; null = anonymous. */
  currentUserId: string | null
  /** Auction owner — needed to project seller vs buyer view. */
  sellerId: string
  /**
   * Optional: a paid-but-unsettled order id for this listing, fetched out of
   * band by the checkout sheet. When set on a winner state, surfaces
   * `existingOrderId` so the sheet can render its retry-settlement UI.
   */
  existingOrderId?: string | null
  /**
   * Server-truth overrides for surfaces that receive these as direct booleans
   * (e.g. checkout sheet via `/current-bid` endpoint, where the secret reserve
   * price is intentionally not echoed back to the client). When set, the
   * override wins over the value computed from raw fields.
   */
  overrides?: {
    reserveNotMet?: boolean
    deadlineExpired?: boolean
  }
  /**
   * Clock source. Defaults to `Date.now`. Tests override it for deterministic
   * deadline / end-time comparisons.
   */
  now?: () => number
}

export function deriveViewerAuctionState(
  inputs: DeriveViewerAuctionStateInputs,
): ViewerAuctionState {
  const {
    slot,
    auction,
    currentUserId,
    sellerId,
    existingOrderId = null,
    overrides,
    now = Date.now,
  } = inputs

  const status = slot?.auctionStatus ?? auction.status
  const highestBid = slot?.highestBid ?? auction.highestBid
  const highestBidderId = slot?.highestBidderId ?? auction.highestBidderId
  const bidCount = slot?.bidCount ?? auction.bidCount
  const endAt = slot?.endAt ?? auction.endAt
  const paymentDeadline = slot?.paymentDeadline ?? auction.paymentDeadline

  const isSeller = currentUserId != null && currentUserId === sellerId
  const isViewerHighestBidder =
    currentUserId != null && currentUserId === highestBidderId

  const hasReserve = auction.reservePrice != null
  const computedReserveNotMet =
    hasReserve &&
    (highestBid == null || highestBid < (auction.reservePrice as number))
  const reserveNotMet = overrides?.reserveNotMet ?? computedReserveNotMet
  // For the live `reserveMet` field: null when no reserve info is available,
  // otherwise the inverse of `reserveNotMet`.
  const reserveMet: boolean | null =
    hasReserve || overrides?.reserveNotMet !== undefined ? !reserveNotMet : null

  const hasBids = bidCount > 0 || highestBid != null

  const nowMs = now()
  const endTimePassed = endAt != null && new Date(endAt).getTime() <= nowMs
  const isEnded = status === "ended" || status === "settled" || endTimePassed
  const isSettled = status === "settled"
  const computedDeadlineExpired =
    paymentDeadline != null && new Date(paymentDeadline).getTime() <= nowMs
  const deadlineExpired = overrides?.deadlineExpired ?? computedDeadlineExpired

  if (!isEnded) {
    if (status === "scheduled") return { kind: "scheduled" }
    return {
      kind: "live",
      canBid: currentUserId != null && !isSeller,
      reserveMet,
    }
  }

  if (isSeller) {
    if (isSettled) return { kind: "ended_seller", subKind: "settled" }
    if (!hasBids) return { kind: "ended_seller", subKind: "no_bids" }
    if (reserveNotMet) return { kind: "ended_seller", subKind: "reserve_not_met" }
    if (deadlineExpired) return { kind: "ended_seller", subKind: "payment_expired" }
    return { kind: "ended_seller", subKind: "sold" }
  }

  if (!hasBids) return { kind: "ended_no_bids" }

  if (reserveNotMet) {
    return {
      kind: "ended_reserve_not_met",
      wasHighestBidder: isViewerHighestBidder,
    }
  }

  if (isViewerHighestBidder) {
    if (isSettled) return { kind: "ended_won_settled", orderId: null }
    if (deadlineExpired) return { kind: "ended_payment_expired" }
    return {
      kind: "ended_won_pending_payment",
      deadline: paymentDeadline,
      canPay: true,
      existingOrderId,
    }
  }

  return { kind: "ended_lost", winnerKnown: highestBidderId != null }
}
